/**
 * Tracks WDIO v9 assertion results via beforeAssertion/afterAssertion hooks.
 */

import type { AppiumAssertionStep } from './types.js';

export interface AssertionHookParams {
  matcherName: string;
  expectedValue?: unknown;
  options: object;
}

export interface AfterAssertionHookParams extends AssertionHookParams {
  result: {
    message: () => string;
    result: boolean;
  };
}

interface PendingAssertion {
  matcherName: string;
  expectedValue?: unknown;
  startTime: number;
}

export class AssertionTracker {
  private readonly steps: AppiumAssertionStep[] = [];
  private pending: PendingAssertion | null = null;
  private enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  beforeAssertion(params: AssertionHookParams): void {
    if (!this.enabled) return;
    this.pending = {
      matcherName: params.matcherName,
      expectedValue: params.expectedValue,
      startTime: Date.now(),
    };
  }

  afterAssertion(params: AfterAssertionHookParams): void {
    if (!this.enabled || !this.pending) return;

    const duration = Date.now() - this.pending.startTime;
    let message: string;
    try {
      message = params.result.message();
    } catch {
      message = '';
    }

    const step: AppiumAssertionStep = {
      type: 'assertion',
      matcherName: this.pending.matcherName,
      expectedValue: this.pending.expectedValue,
      passed: params.result.result,
      message,
      timestamp: new Date(this.pending.startTime).toISOString(),
      duration,
    };

    this.steps.push(step);
    this.pending = null;
  }

  getSteps(): AppiumAssertionStep[] {
    return [...this.steps];
  }

  clear(): void {
    this.steps.length = 0;
    this.pending = null;
  }

  dispose(): void {
    this.clear();
  }
}
